import { useState, type Dispatch, type SetStateAction } from 'react';
import {
    GridRowModes,
    GridRowEditStopReasons,
    type GridRowId,
    type GridRowModesModel,
    type GridEventListener
} from '../lib/dataGrid';

/**
 * The inline row-edit + draft machinery shared by the Zeiterfassung and Phasen
 * grids: an unsaved new row lives as a local `draft` under a sentinel `draftId`
 * while the grid holds it in edit mode. Save/Cancel are explicit (clicking away
 * never commits — `handleRowEditStop` blocks the focus-out reason); Escape or
 * Cancel on the draft discards it.
 *
 * `TDraft` is whatever the grid keeps as its pending row; this hook only tracks
 * it and the row modes.
 */
export function useInlineRowEditing<TDraft>(draftId: GridRowId): {
    rowModesModel: GridRowModesModel;
    setRowModesModel: Dispatch<SetStateAction<GridRowModesModel>>;
    draft: TDraft | null;
    setDraft: Dispatch<SetStateAction<TDraft | null>>;
    isEditing: (id: GridRowId) => boolean;
    startDraft: (draft: TDraft, fieldToFocus: string) => void;
    requestSave: (id: GridRowId) => void;
    cancel: (id: GridRowId) => void;
    handleRowEditStop: GridEventListener<'rowEditStop'>;
} {
    const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
    const [draft, setDraft] = useState<TDraft | null>(null);

    function isEditing(id: GridRowId): boolean {
        return rowModesModel[id]?.mode === GridRowModes.Edit;
    }

    // Open a new draft row for editing, focusing the given field. No-op while a
    // draft is already pending.
    function startDraft(next: TDraft, fieldToFocus: string) {
        if (draft) {
            return;
        }
        setDraft(next);
        setRowModesModel((model) => ({
            ...model,
            [draftId]: { mode: GridRowModes.Edit, fieldToFocus }
        }));
    }

    // Request a commit; the grid keeps the row in edit if a cell fails its
    // preProcessEditCellProps validation, so no invalid row is saved.
    function requestSave(id: GridRowId) {
        setRowModesModel((model) => ({
            ...model,
            [id]: { mode: GridRowModes.View }
        }));
    }

    function cancel(id: GridRowId) {
        setRowModesModel((model) => ({
            ...model,
            [id]: { mode: GridRowModes.View, ignoreModifications: true }
        }));
        if (id === draftId) {
            setDraft(null);
        }
    }

    const handleRowEditStop: GridEventListener<'rowEditStop'> = (
        params,
        event
    ) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        } else if (
            params.reason === GridRowEditStopReasons.escapeKeyDown &&
            params.id === draftId
        ) {
            setDraft(null);
        }
    };

    return {
        rowModesModel,
        setRowModesModel,
        draft,
        setDraft,
        isEditing,
        startDraft,
        requestSave,
        cancel,
        handleRowEditStop
    };
}
